import {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import ShortcutsContext from './ShortcutsContext';
import { KeyPressListener, NewShortcuts, ShortcutsConfigProps } from './types';
import { INITIAL_SHORTCUTS_CONFIG } from './constants';

export const useShortcuts = (
  shortcutConfig: ShortcutsConfigProps['name'][],
  enabled = true,
) => {
  const addShortcut = useContext(ShortcutsContext);

  useEffect(() => {
    if (!enabled) return;

    addShortcut(shortcutConfig);
  }, [addShortcut, enabled, shortcutConfig]);
};

const ShortcutsListener = ({ children }: PropsWithChildren) => {
  const [shortcutsConfig, setShortcutsConfig] = useState(
    INITIAL_SHORTCUTS_CONFIG,
  );

  const isKeyPressed = useCallback(
    (event, key) => event[key] || event.code === key,
    [],
  );

  const useShortcut = useCallback((newShortcuts: NewShortcuts) => {
    setShortcutsConfig((prev) => ({
      ...prev,
      ...newShortcuts.reduce(
        (result, { keyCombo, action }) => ({
          ...result,
          [keyCombo.join('/')]: { keyCombo, action },
        }),
        {},
      ),
    }));
  }, []);

  useEffect(() => {
    const listenersArray: KeyPressListener[] = [];

    Object.values(shortcutsConfig).forEach((shortcut) => {
      const listener = (event) => {
        const {
          keyCombo: [keyA, keyB, keyC],
          action,
        } = shortcut;

        if (
          isKeyPressed(event, keyA) &&
          isKeyPressed(event, keyB) &&
          (keyC ? isKeyPressed(event, keyC) : true)
        ) {
          action();
        }
      };

      document.addEventListener('keypress', listener);
      listenersArray.push(listener);
    });

    return () => {
      listenersArray.forEach((listener) => {
        document.removeEventListener('keypress', listener);
      });
    };
  }, [isKeyPressed, shortcutsConfig]);

  return (
    <ShortcutsContext.Provider value={useShortcut}>
      {children}
    </ShortcutsContext.Provider>
  );
};

export default ShortcutsListener;
